import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Segmented, Flex, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, rgbaColor, numberFormatter, formatCurrency } from '../../../helpers/utils';
import SnapshotService from '../services';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

function snapshotRowKey(groupBy, row) {
  if (groupBy === 'day') {
    const d = row.date_issued;
    if (!d) return '';
    const s = typeof d === 'string' ? d : dayjs(d).format('YYYY-MM-DD');
    return s.slice(0, 10);
  }
  if (groupBy === 'week') return String(row.week);
  if (groupBy === 'month') return row.year_month || '';
  return String(row.year ?? '');
}

function compareBucketKeys(groupBy, a, b) {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  if (groupBy === 'day' || groupBy === 'month') return String(a).localeCompare(String(b));
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b));
}

function formatWeekLabel(yw) {
  const num = parseInt(yw, 10);
  if (Number.isNaN(num)) return String(yw);
  if (num < 10000) return String(num);
  const year = Math.floor(num / 100);
  const w = num % 100;
  return `${year} · W${String(w).padStart(2, '0')}`;
}

function formatBucketLabel(groupBy, k) {
  if (groupBy === 'day') {
    return dayjs(k).isValid() ? dayjs(k).format('MMM D, YYYY') : k;
  }
  if (groupBy === 'month') {
    if (!k || typeof k !== 'string') return '';
    try {
      return dayjs(`${k}-01`).format('MMM YYYY');
    } catch {
      return k;
    }
  }
  if (groupBy === 'week') return formatWeekLabel(k);
  return String(k);
}

function mergeCashFlowBuckets(salesRows, billsRows, groupBy) {
  const toMap = (rows) => {
    const map = new Map();
    rows.forEach((item) => {
      const key = snapshotRowKey(groupBy, item);
      if (!key) return;
      const v = parseFloat(item.total) || 0;
      map.set(key, (map.get(key) || 0) + v);
    });
    return map;
  };
  const salesMap = toMap(salesRows || []);
  const billsMap = toMap(billsRows || []);
  const keys = Array.from(new Set([...salesMap.keys(), ...billsMap.keys()])).sort((a, b) =>
    compareBucketKeys(groupBy, a, b)
  );
  const labels = keys.map((k) => formatBucketLabel(groupBy, k));
  const inflow = keys.map((k) => salesMap.get(k) || 0);
  const outflow = keys.map((k) => -(billsMap.get(k) || 0));
  const netChange = keys.map((_, idx) => inflow[idx] + outflow[idx]);
  return { labels, inflow, outflow, netChange };
}

const CashFlowChart = ({
  startDate,
  endDate,
  onViewReport,
  /** When true: use startDate/endDate + groupBy; hide the rolling 12/24 month segmented control */
  hidePeriodSelector,
  /** 'day' | 'week' | 'month' — required when hidePeriodSelector is true */
  groupBy: groupByProp
}) => {
  const [currency_symbol, setCurrencySymbol] = useState('$');
  const [inflowData, setInflowData] = useState([]);
  const [outflowData, setOutflowData] = useState([]);
  const [netChangeData, setNetChangeData] = useState([]);
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('last_12_months');

  const getData = useCallback(() => {
    setIsLoading(true);

    const now = new Date();
    let rangeStartIso;
    let rangeEndIso;
    let groupByUsed = 'month';

    if (hidePeriodSelector && startDate && endDate) {
      rangeStartIso = startDate;
      rangeEndIso = endDate;
      groupByUsed = groupByProp || 'month';
    } else {
      groupByUsed = 'month';
      if (period === 'last_12_months') {
        const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        rangeStartIso = startDate || start.toISOString().split('T')[0];
        rangeEndIso = endDate || end.toISOString().split('T')[0];
      } else {
        const start = new Date(now.getFullYear(), now.getMonth() - 23, 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        rangeStartIso = startDate || start.toISOString().split('T')[0];
        rangeEndIso = endDate || end.toISOString().split('T')[0];
      }
    }

    const salesParams = {
      group_by: groupByUsed,
      start_date: rangeStartIso,
      end_date: rangeEndIso
    };

    const billsParams = {
      group_by: groupByUsed,
      start_date: rangeStartIso,
      end_date: rangeEndIso
    };

    Promise.all([
      SnapshotService.getSalesSnapshots(salesParams),
      SnapshotService.getBillSnapshots(billsParams)
    ])
      .then(([salesResponse, billsResponse]) => {
        try {
          const salesData =
            salesResponse.data?.success && salesResponse.data?.data?.data
              ? salesResponse.data.data.data
              : [];
          const billsData =
            billsResponse.data?.success && billsResponse.data?.data?.data
              ? billsResponse.data.data.data
              : [];
          const currency =
            salesResponse.data?.data?.currency ||
            billsResponse.data?.data?.currency || { currency_symbol: '$', currency_code: 'CAD' };

          setCurrencySymbol(currency.currency_symbol || '$');

          const { labels: lbls, inflow, outflow, netChange } = mergeCashFlowBuckets(
            salesData,
            billsData,
            groupByUsed
          );

          setLabels(lbls);
          setInflowData(inflow);
          setOutflowData(outflow);
          setNetChangeData(netChange);
          setIsLoading(false);
        } catch (error) {
          console.error('Error processing cash flow data:', error);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching cash flow data:', error);
        setIsLoading(false);
      });
  }, [period, startDate, endDate, hidePeriodSelector, groupByProp]);

  useEffect(() => {
    getData();
  }, [getData]);

  const changePeriod = useCallback((value) => {
    setPeriod(value);
  }, []);

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const firstParam = params[0];
    const periodLabel = firstParam.name || '';
    
    let inflow = 0;
    let outflow = 0;
    let netChange = 0;
    
    params.forEach(param => {
      const value = typeof param.value === 'object' ? param.value[1] : param.value;
      const numValue = parseFloat(value) || 0;
      
      if (param.seriesName === 'Inflow') {
        inflow = numValue;
      } else if (param.seriesName === 'Outflow') {
        outflow = Math.abs(numValue); // Convert back to positive for display
      } else if (param.seriesName === 'Net Change') {
        netChange = numValue;
      }
    });

    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${periodLabel}</div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('primary')}; margin-right: 6px;"></span>
          Inflow: ${formatCurrency(inflow, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('gray-400')}; margin-right: 6px;"></span>
          Outflow: ${formatCurrency(outflow, currency_symbol)}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('dark')}; margin-right: 6px;"></span>
          Net Change: ${formatCurrency(netChange, currency_symbol)}
        </div>
      </div>
    `;
  }, [currency_symbol]);

  const getOptions = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      formatter: tooltipFormatter,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: { color: '#fff' }
    },
    legend: {
      data: ['Inflow', 'Outflow', 'Net Change'],
      bottom: 0,
      textStyle: {
        color: getColor('gray-400')
      }
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisPointer: { type: 'shadow' },
      axisLabel: {
        color: getColor('gray-400'),
        rotate: labels.length > 12 ? 45 : 0
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisPointer: { show: false },
      splitLine: {
        lineStyle: {
          color: getColor('gray-300'),
          type: 'dashed'
        }
      },
      axisLabel: {
        show: true,
        color: getColor('gray-400'),
        formatter: (value) => {
          return `${currency_symbol} ${numberFormatter(value, 0)}`
        },
        margin: 15
      },
      axisTick: { show: false },
      axisLine: { show: false }
    },
    series: [
      {
        name: 'Inflow',
        type: 'bar',
        data: inflowData,
        itemStyle: {
          color: getColor('primary')
        },
        stack: 'cashflow',
        barMaxWidth: 50
      },
      {
        name: 'Outflow',
        type: 'bar',
        data: outflowData,
        itemStyle: {
          color: getColor('gray-400')
        },
        stack: 'cashflow',
        barMaxWidth: 50
      },
      {
        name: 'Net Change',
        type: 'line',
        data: netChangeData,
        lineStyle: {
          color: getColor('dark'),
          width: 2
        },
        itemStyle: {
          color: getColor('dark'),
          borderWidth: 2
        },
        symbol: 'circle',
        symbolSize: 6,
        yAxisIndex: 0
      }
    ],
    grid: { right: 10, left: 0, bottom: 50, top: 10, containLabel: true }
  }), [labels, currency_symbol, tooltipFormatter, inflowData, outflowData, netChangeData]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  return (
    <>
      <div>
        {/* <h4 style={{ margin: 0, marginBottom: '4px' }}>Cash Flow</h4>
        <p style={{ margin: 0, color: getColor('gray-600'), fontSize: '14px' }}>
          Cash coming in and going out of your business.
        </p> */}
      </div>
      
      <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
        <div />
        <Flex gap="middle" align="center">
          {!hidePeriodSelector && (
            <Segmented
              options={[
                { label: 'Last 12 Months', value: 'last_12_months' },
                { label: 'Last 24 Months', value: 'last_24_months' }
              ]}
              value={period}
              onChange={changePeriod}
            />
          )}
          {onViewReport && (
            <Button type="link" onClick={onViewReport}>
              View Report
            </Button>
          )}
        </Flex>
      </Flex>
      
      <ReactEChartsCore
        echarts={echarts}
        option={getOptions}
        style={{ height: '300px' }}
      />
    </>
  );
};

export default CashFlowChart;

